import { useEffect, useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { PublicReceipt } from "@/components/public/PublicReceipt";
import { toast } from "sonner";

const field = "w-full px-3 py-2.5 text-sm rounded-sm border outline-none bg-white text-[#12263A] placeholder:text-[#94A3B8] focus:border-[#12263A]";
export const L = ({ children }) => <label className="block text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: P.dim }}>{children}</label>;

export const CategoryPicker = ({ options, selected, onToggle, testPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
    {options.map((c) => (
      <button key={c} type="button" data-testid={`${testPrefix}-cat-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        onClick={() => onToggle(c)}
        className="text-left px-3 py-2 text-sm rounded-sm border transition-colors"
        style={selected.includes(c) ? { background: P.navy, color: "#fff", borderColor: P.navy } : { background: "#fff", color: P.ink, borderColor: P.line }}>
        {selected.includes(c) ? "✓ " : ""}{c}
      </button>
    ))}
  </div>
);

export default function ComplaintForm({ onNavigate }) {
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState([]);
  const [anon, setAnon] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  useEffect(() => { pub.get("/categories").then((r) => setCats(r.data.complaint)).catch(() => {}); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (c) => setSel((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const submit = async () => {
    if (!sel.length) { toast.error("Select at least one complaint category."); return; }
    if ((f.description || "").trim().length < 20) { toast.error("Please describe the incident (at least 20 characters)."); return; }
    if (!anon && !(f.name && (f.email || f.phone))) { toast.error("Provide your name and an email or phone — or choose to remain anonymous."); return; }
    setBusy(true);
    try {
      const r = await pub.post("/cases", { kind: "complaint", categories: sel, anonymous: anon, ...f });
      setReceipt(r.data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed — please try again."); }
    finally { setBusy(false); }
  };
  if (receipt) return <PublicReceipt result={receipt} onNavigate={onNavigate} />;
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <img src="/brand/ia-seal.png" alt="Matcom Internal Affairs seal" data-testid="complaint-ia-seal" className="w-20 h-20 rounded-full shrink-0" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: P.gold }}>Matcom Internal Affairs</div>
          <h2 className="text-2xl font-bold" style={{ color: P.navy }}>File a Complaint</h2>
          <p className="text-sm mt-1" style={{ color: P.dim }}>One incident may include several alleged violations — select every category that applies. Your complaint will be reviewed by the agency's professional standards process.</p>
        </div>
      </div>
      <label data-testid="complaint-anonymous-toggle" className="flex items-start gap-3 p-4 rounded-sm border cursor-pointer" style={{ borderColor: anon ? P.navy : P.line, background: anon ? "#EEF2F6" : "#fff" }}>
        <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} className="mt-1" />
        <span className="text-sm" style={{ color: P.ink }}>
          <b>I wish to remain anonymous.</b>
          <span className="block text-xs mt-0.5" style={{ color: P.dim }}>Anonymous complaints are accepted; however, if we cannot contact you, our ability to follow up or gather additional details may be limited.</span>
        </span>
      </label>
      {!anon && (
        <div className="p-4 rounded-sm border space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
          <div className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Your Contact Information</div>
          <div><L>Full Name *</L><input data-testid="complaint-name" className={field} style={{ borderColor: P.line }} value={f.name || ""} onChange={set("name")} placeholder="Your full name" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><L>Email</L><input data-testid="complaint-email" className={field} style={{ borderColor: P.line }} value={f.email || ""} onChange={set("email")} placeholder="you@email.com" /></div>
            <div><L>Telephone</L><input data-testid="complaint-phone" className={field} style={{ borderColor: P.line }} value={f.phone || ""} onChange={set("phone")} placeholder="(555) 123-4567" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><L>Address (optional)</L><input className={field} style={{ borderColor: P.line }} value={f.address || ""} onChange={set("address")} /></div>
            <div><L>Preferred Contact Method</L>
              <select data-testid="complaint-contact-method" className={field} style={{ borderColor: P.line }} value={f.preferred_contact || ""} onChange={set("preferred_contact")}>
                <option value="">Select…</option><option>Email</option><option>Phone</option><option>Mail</option>
              </select></div>
          </div>
        </div>
      )}
      <div>
        <L>Complaint Categories * — select all that apply ({sel.length} selected)</L>
        <CategoryPicker options={cats} selected={sel} onToggle={toggle} testPrefix="complaint" />
      </div>
      <div className="p-4 rounded-sm border space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: P.navy }}>Incident Details</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><L>Incident Date</L><input data-testid="complaint-date" type="date" className={field} style={{ borderColor: P.line }} value={f.incident_date || ""} onChange={set("incident_date")} /></div>
          <div><L>Incident Time</L><input type="time" className={field} style={{ borderColor: P.line }} value={f.incident_time || ""} onChange={set("incident_time")} /></div>
          <div><L>CAD Incident # (if known)</L><input className={field} style={{ borderColor: P.line }} value={f.cad_number || ""} onChange={set("cad_number")} placeholder="2026-#####" /></div>
        </div>
        <div><L>Incident Location</L><input data-testid="complaint-location" className={field} style={{ borderColor: P.line }} value={f.incident_location || ""} onChange={set("incident_location")} placeholder="Address or location" /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><L>Officer/Employee Name (if known)</L><input data-testid="complaint-officer" className={field} style={{ borderColor: P.line }} value={f.officer_name || ""} onChange={set("officer_name")} /></div>
          <div><L>Badge/Employee # (if known)</L><input className={field} style={{ borderColor: P.line }} value={f.badge_number || ""} onChange={set("badge_number")} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><L>Unit # (if known)</L><input className={field} style={{ borderColor: P.line }} value={f.unit_number || ""} onChange={set("unit_number")} /></div>
          <div><L>Vehicle # (if known)</L><input className={field} style={{ borderColor: P.line }} value={f.vehicle_number || ""} onChange={set("vehicle_number")} /></div>
        </div>
        <div><L>Witness Information</L><textarea className={field} style={{ borderColor: P.line }} rows={2} value={f.witnesses || ""} onChange={set("witnesses")} placeholder="Names / contact information of any witnesses" /></div>
      </div>
      <div><L>Description of Incident *</L>
        <textarea data-testid="complaint-description" className={field} style={{ borderColor: P.line }} rows={6} value={f.description || ""} onChange={set("description")} placeholder="Please describe what happened, in as much detail as you can. Include what was said and done, and by whom." /></div>
      <div><L>Requested Outcome</L>
        <textarea className={field} style={{ borderColor: P.line }} rows={2} value={f.requested_outcome || ""} onChange={set("requested_outcome")} placeholder="What outcome are you seeking?" /></div>
      <button data-testid="complaint-submit" disabled={busy} onClick={submit}
        className="w-full py-3.5 text-sm font-bold text-white rounded-sm hover:opacity-90 disabled:opacity-50" style={{ background: P.red }}>
        {busy ? "SUBMITTING…" : "SUBMIT COMPLAINT"}
      </button>
      <p className="text-xs text-center" style={{ color: P.dim }}>A case number, secure access code and electronic receipt will be generated the moment you submit. Evidence can be uploaded afterward using your case number.</p>
    </div>
  );
}
